import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Loader2, Palette } from 'lucide-react';
import ImageEditor from './ImageEditor';
import { imageStudioService } from '../../services/imageStudioService';
import { GeneratedImage } from '../../types/imageStudio';
import toast from 'react-hot-toast';

interface ImageEditorModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (imageUrl: string, image?: GeneratedImage) => void;
  /** URL of the image to edit — will be uploaded to Image Studio first */
  imageUrl?: string;
  /** Or pass an existing GeneratedImage directly */
  image?: GeneratedImage;
}

const ImageEditorModal: React.FC<ImageEditorModalProps> = ({
  isOpen,
  onClose,
  onSave,
  imageUrl,
  image: imageProp
}) => {
  const { t } = useTranslation();
  const [image, setImage] = useState<GeneratedImage | null>(imageProp || null);
  const [loading, setLoading] = useState(false);
  // Após "remover fundo" o editor troca a camada internamente (sem remontar);
  // aqui só guardamos o id do registro mais recente para o save versionar certo.
  const latestImageIdRef = React.useRef<string | null>(null);

  // If we receive a URL but no image record, upload it first to create a record
  useEffect(() => {
    if (!isOpen) {
      setImage(null);
      setLoading(false);
      latestImageIdRef.current = null;
      return;
    }
    if (imageProp) {
      setImage(imageProp);
      return;
    }
    if (imageUrl) {
      setImage(null);
      uploadFromUrl(imageUrl);
    }
  }, [isOpen, imageUrl, imageProp]);

  const uploadFromUrl = async (url: string) => {
    setLoading(true);
    try {
      // Strategy 1: Fetch the image and upload as file
      try {
        const resp = await fetch(url);
        if (resp.ok) {
          const blob = await resp.blob();
          if (blob.size > 100) {
            const file = new File([blob], `edit-${Date.now()}.png`, { type: blob.type || 'image/png' });
            const uploadResp = await imageStudioService.uploadImage(file, { title: 'Editing...' });
            if (uploadResp.success && uploadResp.data) {
              setImage(uploadResp.data);
              return;
            }
          }
        }
      } catch {
        // Fetch failed (CORS) — try next strategy
      }

      // Strategy 2: Send URL to backend (JSON body, no multipart), let it store the URL reference
      const { default: api } = await import('../../services/api');
      const directResp = await api.post('/image-studio/upload-url', { url, title: 'Editing...' });
      if (directResp.data?.success && directResp.data?.data) {
        setImage(directResp.data.data);
        return;
      }

      toast.error('Failed to load image for editing');
      onClose();
    } catch {
      toast.error('Failed to load image for editing');
      onClose();
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (dataUrl: string, width: number, height: number, mimeType: string) => {
    if (!image) return;
    try {
      const res = await fetch(dataUrl);
      const blob = await res.blob();
      const file = new File([blob], `edited-${Date.now()}.${mimeType === 'image/jpeg' ? 'jpg' : 'png'}`, { type: mimeType });

      const response = await imageStudioService.saveEdited(latestImageIdRef.current || image.id, {
        file,
        width,
        height,
        mime_type: mimeType,
        title: image.title
      });

      if (response.success && response.data) {
        const url = response.data.original_url || '';
        toast.success(t('imageStudio.saved', 'Image saved!'));
        onSave(url, response.data);
        onClose();
      }
    } catch {
      toast.error(t('imageStudio.saveError', 'Failed to save'));
    }
  };

  const handleBgRemoved = (newImage: GeneratedImage) => {
    // NÃO usar setImage aqui: remontaria o ImageEditor e perderia camadas não salvas
    latestImageIdRef.current = newImage.id;
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-gray-900/95 backdrop-blur-sm">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-2 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 shrink-0">
        <h2 className="text-sm font-semibold text-gray-900 dark:text-white flex items-center gap-2">
          <Palette className="w-4 h-4 text-primary-600" />
          {t('imageStudio.editor.title', 'Image Editor')}
        </h2>
        <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors">
          <X className="w-5 h-5 text-gray-500" />
        </button>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-center">
              <Loader2 className="w-8 h-8 animate-spin text-primary-500 mx-auto mb-2" />
              <p className="text-sm text-gray-400">Loading editor...</p>
            </div>
          </div>
        ) : image ? (
          <ImageEditor image={image} onSave={handleSave} onBgRemoved={handleBgRemoved} />
        ) : (
          <div className="flex items-center justify-center h-full">
            <p className="text-gray-400">No image to edit</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ImageEditorModal;
